<script>
import LinkBranchStaffModal from '../branches/LinkBranchStaffModal.vue';
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';
import AoikumoIntegrationTag from 'dashboard/components/dahreply/AoikumoIntegrationTag.vue';

export default {
  name: 'ServiceCard',
  components: {
    LinkBranchStaffModal,
    AoikumoDisabledTooltip,
    AoikumoIntegrationTag,
  },
  props: {
    service: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false
    },
    isBranch: {
      type: Boolean,
      default: false,
    },
    branchId: {
      type: String,
      default: '',
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isHovered: false,
      previewImage: null,
      showLinkModal: false,
      lastModalInteractionTime: 0,
    };
  },
  computed: {
    isOnboardingLinkStaffToService() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'link_staff_to_service';
    },
    serviceData() {
      return this.service.service || {};
    },
    serviceBranches() {
      return this.service.branches || [];
    },
    serviceStaff() {
      return this.service.staff || [];
    },
    branchServiceStaff() {
      if (!this.branchId) return [];
      return this.service.staff.filter(s => 
        s.branches && s.branches.some(b => b.id === this.branchId)
      ) || [];
    },
    serviceId() {
      return this.serviceData.id;
    },
    serviceName() {
      return this.serviceData.name;
    },
    duration() {
      return this.serviceData.duration;
    },
    maxServe() {
      return this.serviceData.max_serve;
    },
    description() {
      return this.serviceData.description;
    },
    pricingDetails() {
      return this.serviceData.price ? `${this.serviceData.price}` : '—';
    },
    imagePreview() {
      return this.serviceData.images[0];
    },
    mediaPreviews() {
      return this.serviceData.images || [];
    },
    isActive() {
      return this.serviceData.is_active;
    },
    disabledReason() {
      return this.serviceData.disabled_reason;
    },
    branchIsActive() {
      return this.service?.assignment?.is_enabled ?? null;
    },
    branchDisabledReason() {
      return this.service?.assignment?.disabled_reason ?? null;
    },
    cardClasses() {
      return {
        'cursor-pointer hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600': !this.loading,
        'animate-pulse': this.loading
      };
    },
    isToggleActive() {
      if (!this.isActive) return false;

      if (this.isBranch) {
        return this.branchIsActive;
      }

      return this.isActive;
    },
    // NEW: Check if service is globally disabled
    isGloballyDisabled() {
      return !this.isActive;
    },
    // NEW: Check if toggle should be shown
    showToggle() {
      // Don't show toggle for branches when service is globally disabled
      if (this.isBranch && this.isGloballyDisabled) {
        return false;
      }
      return true;
    },
    // NEW: Determine which disabled reason to show
    activeDisabledReason() {
      if (this.isGloballyDisabled) {
        return this.disabledReason;
      }
      if (this.isBranch && !this.isToggleActive) {
        return this.branchDisabledReason;
      }
      return null;
    },
    // NEW: Determine banner color based on disable type
    disabledBannerColor() {
      if (this.isGloballyDisabled) {
        return 'bg-red-600'; // Global disable - red
      }
      if (this.isBranch && !this.isToggleActive) {
        return 'bg-yellow-600'; // Branch disable - yellow
      }
      return '';
    },
    ariaLabel() {
      return `${this.serviceName} service`;
    }
  },
  methods: {
     onToggle(event) {
      if (this.loading || this.readOnly) return;
      event.stopPropagation();
      this.$emit('toggle', this.service);
    },
    onEdit(event) {
      if (this.loading || this.readOnly) return;
      event.stopPropagation();
      this.$emit('edit', this.serviceData);
    },
    onDelete(event) {
      if (this.loading || this.readOnly) return;
      event.stopPropagation();
      this.$emit('delete', {
        name: this.serviceName,
        id: this.serviceId
      });
    },
    onNavigate() {
      if (this.loading) return;
      // Prevent navigation if modal was just closed (within 100ms)
      if (Date.now() - this.lastModalInteractionTime < 100) return;
      this.$emit('navigate', this.serviceId);
    },
    onMouseEnter() {
      if (!this.loading) this.isHovered = true;
    },
    onMouseLeave() {
      this.isHovered = false;
    },
    openPreview(url) {
      this.previewImage = url;
    },
    closePreview() {
      this.previewImage = null;
    },
    openLinkModal(event) {
      event.stopPropagation();
      this.showLinkModal = true;
      this.lastModalInteractionTime = Date.now();
    },
    closeLinkModal() {
      this.showLinkModal = false;
      this.lastModalInteractionTime = Date.now();
    },
  },
  emits: ['edit', 'delete', 'navigate', 'toggle']
};
</script>

<template>
  <div
    class="relative bg-white dark:bg-slate-800 dark:text-white rounded-lg shadow-md border border-slate-200 dark:border-slate-700 transition-all duration-200 overflow-hidden"
    :class="[cardClasses, ( !isToggleActive ) ? 'opacity-60' : '']"
    @click="onNavigate"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    :aria-label="ariaLabel"
    role="button"
    tabindex="0"
    @keydown.enter="onNavigate"
    @keydown.space="onNavigate"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-white dark:bg-slate-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10"
    >
      <fluent-icon icon="spinner" class="animate-spin h-6 w-6 text-primary" />
    </div>

    <!-- Unified Disabled Reason Banner -->
    <div
      v-if="activeDisabledReason"
      class="absolute bottom-0 left-0 right-0 text-white text-xs text-center py-1 z-10"
      :class="disabledBannerColor"
    >
      {{ activeDisabledReason }}
    </div>

    <!-- Service Image -->
    <div class="relative h-48 bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <img
        v-if="imagePreview"
        :src="imagePreview.presigned_url"
        :alt="serviceName"
        class="w-full h-full object-cover transition-transform duration-300"
        :class="{ 'scale-105': isHovered }"
      />
      <div
        v-else
        class="w-full h-full flex items-center justify-center text-slate-400"
      >
        <fluent-icon icon="image" class="h-12 w-12" />
      </div>
      
      <!-- Duration Badge -->
      <div
        v-if="duration"
        class="absolute top-3 left-3 bg-slate-900 bg-opacity-70 text-white px-2 py-1 rounded-md text-xs font-medium"
      >
        {{ duration * 60 }} mins
      </div>
      
      <!-- Max Serve Badge -->
      <div
        v-if="maxServe"
        class="absolute top-3 right-3 bg-slate-900 bg-opacity-70 text-white px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1"
      >
        <fluent-icon icon="people" class="h-3 w-3" />
        <span>Max {{ maxServe }}</span>
      </div>
    </div>

    <!-- Hover Actions -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <AoikumoDisabledTooltip
        v-if="isHovered && !loading && showToggle"
        :disabled="readOnly"
        class="absolute top-3 right-3 flex gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg p-1 z-20"
        role="toolbar"
        aria-label="Service actions"
      >
        <!-- Toggle Button - Conditionally Rendered -->
        <button
          :disabled="readOnly"
          @click.stop="onToggle"
          :class="[
            'p-2 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50',
            isToggleActive
              ? 'text-woot-600 dark:text-woot-400 hover:bg-woot-100 dark:hover:bg-woot-900/30'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600'
          ]"
          :aria-label="isToggleActive ? 'Disable service' : 'Enable service'"
          :title="isToggleActive ? 'Disable service' : 'Enable service'"
        >
          <fluent-icon
            :icon="isToggleActive ? 'toggle-right' : 'toggle-left'"
            class="h-4 w-4"
          />
        </button>

        <!-- Edit Button -->
        <button
          v-if="!isBranch"
          :disabled="readOnly"
          @click.stop="onEdit"
          class="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
          aria-label="Edit"
          title="Edit"
        >
          <fluent-icon icon="edit" class="h-4 w-4" />
        </button>

        <!-- Delete Button -->
        <button
          v-if="!isBranch"
          :disabled="readOnly"
          @click.stop="onDelete"
          class="p-2 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
          aria-label="Delete"
          title="Delete"
        >
          <fluent-icon icon="delete" class="h-4 w-4" />
        </button>
      </AoikumoDisabledTooltip>
    </transition>

    <!-- Content -->
    <div class="p-5">
      <!-- Header with Service Name and Price -->
      <div class="flex flex-col mb-3 gap-2">
        <h4 
          class="text-lg font-semibold text-slate-800 dark:text-white line-clamp-2 break-words"
          :title="serviceName"
        >
          {{ serviceName }}
        </h4>
        <AoikumoIntegrationTag :item="serviceData" />
        
        <!-- Price - Moved below title and allowed to wrap -->
        <div class="text-lg font-bold text-primary break-words leading-tight">
          {{ pricingDetails }}
        </div>
      </div>
      
      <!-- Description -->
      <p 
        v-if="description" 
        class="text-sm text-slate-600 dark:text-slate-300 mb-2 line-clamp-2 break-words"
      >
        {{ description }}
      </p>

      <!-- Staff and branches -->
      <div class="text-sm text-slate-600 dark:text-slate-300 space-y-1 mb-4">
        <!-- Branches -->
        <div v-if="!isBranch" class="flex items-start gap-2">
          <fluent-icon icon="building-bank" class="h-4 w-4 mt-0.5 flex-shrink-0 text-woot-400" />
          <span class="truncate break-words line-clamp-1">
            {{ serviceBranches.length ? serviceBranches.map(b => b.name).join(', ') : '-' }}
          </span>
        </div>

        <!-- Staff -->
        <div v-if="!isBranch" class="flex items-start gap-2">
          <fluent-icon icon="person" class="h-4 w-4 mt-0.5 flex-shrink-0 text-woot-400" />
          <span class="truncate break-words line-clamp-1">
            {{ serviceStaff.length ? serviceStaff.map(s => s.name).join(', ') : '-' }}
          </span>
        </div>

        <div v-else class="flex items-center gap-2">
          <fluent-icon icon="person" class="h-4 w-4 mt-0.5 flex-shrink-0 text-woot-400" />
          <span class="truncate break-words line-clamp-1">
            {{ branchServiceStaff.length ? branchServiceStaff.map(s => s.name).join(', ') : '-' }}
          </span>
          <div class="relative inline-block">
            <button
              v-if="isBranch"
              @click.stop="openLinkModal"
              class="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              aria-label="Link staff"
              title="Link staff"
            >
              <fluent-icon icon="edit" class="h-4 w-4" />
            </button>
            <!-- Onboarding Hint Tooltip -->
            <div v-if="isOnboardingLinkStaffToService && isFirst" class="absolute -bottom-10 left-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
              <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
                <fluent-icon icon="caret-up" /> Hint: Click to link staff to this service 
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Media Previews -->
      <div v-if="mediaPreviews.length > 0" class="flex gap-2 mb-4">
        <div
          v-for="(media, index) in mediaPreviews.slice(1, 4)"
          :key="index"
          class="relative h-16 w-16 rounded-md overflow-hidden border border-slate-200 dark:border-slate-600 cursor-zoom-in flex-shrink-0"
          @click.stop="openPreview(media.presigned_url)"
        >
          <img :src="media.presigned_url" class="w-full h-full object-cover" />
          <div
            v-if="index === 2 && mediaPreviews.length > 3"
            class="absolute inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center text-white text-xs font-medium"
          >
            +{{ mediaPreviews.length - 3 }}
          </div>
        </div>
      </div>

      <!-- Image Preview Modal -->
      <div
        v-if="previewImage"
        class="fixed inset-0 bg-slate-900/70 flex items-center justify-center z-50"
        @click="closePreview"
      >
        <img :src="previewImage" class="max-h-[90vh] max-w-[90vw] rounded-lg shadow-lg" />
      </div>
    </div>
    
    <!-- Link Staff Modal -->
    <LinkBranchStaffModal
      v-if="showLinkModal && isBranch"
      :staffs="branchServiceStaff"
      :parent-service-id="serviceId"
      @click.stop
      @close="closeLinkModal"
      @refresh="$emit('refresh')"
    />
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Focus styles for accessibility */
[role="button"]:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Ensure the action buttons are properly sized */
button {
  min-width: 2rem;
  min-height: 2rem;
}
</style>